/* eslint-disable @typescript-eslint/no-explicit-any */
import { toast } from "react-toastify";

export interface ApiResponse<T = any> {
  value: T;
  message: string;
  status: number;
  pagination?: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Status code messages mapping
 */
const STATUS_MESSAGES: Record<number, string> = {
  400: "Bad request. Please check your input.",
  401: "Unauthorized. Please login again.",
  403: "Forbidden. You don't have permission.",
  404: "Resource not found.",
  409: "Conflict. Resource already exists.",
  413: "File is too large. Please use a smaller file.",
  422: "Validation error. Please check your input.",
  429: "Too many requests. Please try again later.",
  500: "Internal server error. Please try again later.",
  502: "Bad gateway. Please try again later.",
  503: "Service unavailable. Please try again later.",
};

/**
 * Get user-friendly message for a status code
 */
const getStatusMessage = (status: number, fallback?: string): string => {
  return STATUS_MESSAGES[status] || fallback || "Something went wrong.";
};

/**
 * Handle API response by status code.
 * - 200/201: returns { ...data, status }
 * - 204: returns empty result with status 204
 * - 4xx/5xx: throws ApiError with status-specific message
 */
export const handleResponse = async <T = any>(
  response: Response,
  context: string = "Request",
): Promise<ApiResponse<T>> => {
  const { status } = response;

  // ✅ 204 No Content
  if (status === 204) {
    return {
      value: [] as any,
      message: "No data found",
      status: 204,
    };
  }

  // Parse JSON body (handle parse failure)
  let data: any;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new ApiError(status, getStatusMessage(status, `${context} failed`));
    }
    return { value: null as any, message: "Success", status };
  }

  // ✅ Success (2xx)
  if (response.ok) {
    return {
      ...data,
      status,
    };
  }

  // ❌ Error responses (4xx, 5xx)
  const serverMessage = data?.message || data?.error;
  const message =
    serverMessage || getStatusMessage(status, `${context} failed`);

  console.error(`[${context}] ${status}:`, message);

  // 401 — Unauthorized: logout and redirect
  if (status === 401) {
    localStorage.removeItem("auth_token");
    toast.error(serverMessage);
    // window.location.href = "/login";
    throw new ApiError(status, message, data);
  }

  // Show toast with server error message for all other errors
  toast.error(message);

  // All other errors
  throw new ApiError(status, message, data);
};

/**
 * Handle API response gracefully (no throw) — returns structured result for list endpoints.
 * Use this for paginated/list APIs where you want to return empty results instead of throwing.
 */
export const handleListResponse = async <T = any>(
  response: Response,
  context: string = "Request",
  defaults?: { page?: number; pageSize?: number },
): Promise<ApiResponse<T>> => {
  const { status } = response;

  // ✅ 204 No Content
  if (status === 204) {
    return {
      value: [] as any,
      message: "No data found",
      status: 204,
      pagination: {
        page: defaults?.page || 1,
        pageSize: defaults?.pageSize || 10,
        total: 0,
        totalPages: 0,
      },
    };
  }

  let data: any;
  try {
    data = await response.json();
  } catch {
    return {
      value: [] as any,
      message: getStatusMessage(status, `${context} failed`),
      status,
      pagination: {
        page: defaults?.page || 1,
        pageSize: defaults?.pageSize || 10,
        total: 0,
        totalPages: 0,
      },
    };
  }

  // ✅ Success (2xx)
  if (response.ok) {
    return { ...data, status };
  }

  // ❌ Error — return structured response (no throw)
  const message =
    data?.message || getStatusMessage(status, `${context} failed`);
  console.error(`[${context}] ${status}:`, message);

  return {
    value: [] as any,
    message,
    status,
    pagination: {
      page: defaults?.page || 1,
      pageSize: defaults?.pageSize || 10,
      total: 0,
      totalPages: 0,
    },
  };
};

/**
 * Wraps an async API call with try/catch and returns a consistent error for network failures.
 */
export const safeApiCall = async <T>(
  fn: () => Promise<T>,
  context: string = "Request",
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    console.error(`[${context}] Network error:`, error);
    throw new ApiError(500, "Server error. Please try again later.");
  }
};
